// apps/web/src/components/users/user-pin-dialog.tsx — Fase 17.9, Plano 02 (D-09).
import { useState, type ReactElement, type FormEvent } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { apiMutate } from '../../lib/csrf-fetch';
import type { UserRow } from './user-table';

export interface UserPinDialogProps {
  readonly aberto: boolean;
  readonly usuario: UserRow | null;
  readonly onFechar: () => void;
  readonly onSucesso: (mensagem: string) => void;
}

export function validarPinAdmin(pin: string, confirmacao: string): string | null {
  if (!pin) {
    return 'Informe o PIN.';
  }
  if (!/^\d{4}$/.test(pin)) {
    return 'O PIN deve conter exatamente 4 dígitos numéricos.';
  }
  if (pin !== confirmacao) {
    return 'A confirmação do PIN não confere.';
  }
  return null;
}

export function UserPinDialog({
  aberto,
  usuario,
  onFechar,
  onSucesso,
}: UserPinDialogProps): ReactElement | null {
  const [pin, setPin] = useState<string>('');
  const [confirmacao, setConfirmacao] = useState<string>('');
  const [mostrarPin, setMostrarPin] = useState<boolean>(false);
  const [salvando, setSalvando] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!aberto || !usuario) return null;

  const temPinAtual = Boolean(usuario.temPin);
  const titulo = temPinAtual ? 'Redefinir PIN de acesso ao tablet' : 'Definir PIN de acesso ao tablet';

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);

    const erroValidacao = validarPinAdmin(pin, confirmacao);
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    setSalvando(true);
    try {
      const res = await apiMutate(`/api/v1/users/${encodeURIComponent(usuario!.id)}/pin`, {
        method: 'POST',
        body: { pin },
      });

      if (!res.ok) {
        const corpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(corpo.message ?? 'Falha ao salvar o PIN do usuário.');
      }

      onSucesso(`PIN de ${usuario!.displayName || usuario!.username} ${temPinAtual ? 'redefinido' : 'definido'} com sucesso.`);
      onFechar();
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao salvar o PIN.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pin-dialog-titulo"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl space-y-5 text-slate-800">
        <div className="flex items-start gap-3 border-b border-slate-100 pb-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h2 id="pin-dialog-titulo" className="text-lg font-bold text-slate-900">
              {titulo}
            </h2>
            <p className="text-xs text-slate-500">
              Usuário: <strong className="text-slate-700">{usuario.displayName}</strong> ({usuario.username})
            </p>
          </div>
        </div>

        <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-lg border border-slate-200">
          O PIN de 4 dígitos numéricos é utilizado para autenticação rápida nos tablets de chão de fábrica (coletor e Kanban).
        </p>

        {erro && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-700" role="alert">
            {erro}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="input-pin" className="text-xs font-bold text-slate-700">
                Novo PIN (4 dígitos)
              </label>
              <button
                type="button"
                onClick={() => setMostrarPin((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800 font-medium"
              >
                {mostrarPin ? (
                  <>
                    <EyeOff className="h-3.5 w-3.5" /> Ocultar
                  </>
                ) : (
                  <>
                    <Eye className="h-3.5 w-3.5" /> Ver dígitos
                  </>
                )}
              </button>
            </div>
            <input
              id="input-pin"
              type={mostrarPin ? 'text' : 'password'}
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="Ex: 7291"
              disabled={salvando}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg font-mono tracking-widest text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              required
            />
          </div>

          <div>
            <label htmlFor="input-confirmar-pin" className="block text-xs font-bold text-slate-700 mb-1">
              Confirmar PIN
            </label>
            <input
              id="input-confirmar-pin"
              type={mostrarPin ? 'text' : 'password'}
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              autoComplete="off"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="Digite o mesmo PIN"
              disabled={salvando}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg font-mono tracking-widest text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              required
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onFechar}
              disabled={salvando}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando || pin.length !== 4 || confirmacao.length !== 4}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {salvando ? 'Salvando...' : 'Salvar PIN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
